// src/components/pages/integrations/OAuthInitiator.tsx

import React, { useState, useContext, useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from "react-native";
import SessionContext from "../../../../contexts/session";
import {
  listIntegrations,
  TIntegration,
} from "../../../../services/api-integrations";
import IntegrationSetupForm from "./IntegrationSetupForm";
import IntegrationManager from "./IntegrationManager";

// Import integration-specific cards
import DrChronoIntegrationCard from "./drchrono/DrChronoIntegrationCard";
import GenericWebhookSetupCard from "./generic-webhook/GenericWebhookSetupCard";

const OAuthInitiatorPage = () => {
  const [integrations, setIntegrations] = useState<TIntegration[]>([]);
  const [showSetupForm, setShowSetupForm] = useState(false);
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;

  // Get URL parameters to determine which integrations to show
  const urlParams = new URLSearchParams(window.location.search);
  const showTopAesthetics =
    urlParams.has("top-aesthetics") || urlParams.has("all");
  const showCameoSurgery = urlParams.has("cameo") || urlParams.has("all");
  const showOnlyWebhook = urlParams.has("webhook");

  const loadIntegrations = async () => {
    if (!session) return;

    try {
      const existingIntegrations = await listIntegrations(session);
      setIntegrations(existingIntegrations);
    } catch (error) {
      console.error("Failed to load integrations:", error);
    }
  };

  useEffect(() => {
    loadIntegrations();
  }, [session]);

  const refreshData = () => {
    loadIntegrations();
    setShowSetupForm(false);
  };

  if (!session) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>
          Please log in to manage integrations
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>🔌 Integrations</Text>
      <Text style={styles.subtitle}>
        Connect your clinic to external systems
      </Text>

      <GenericWebhookSetupCard session={session} onSuccess={refreshData} />

      {/* Integration-specific cards */}
      {!showOnlyWebhook && (
        <DrChronoIntegrationCard
          integrations={integrations}
          showTopAesthetics={showTopAesthetics}
          showCameoSurgery={showCameoSurgery}
          session={session}
        />
      )}

      {/* Future integrations would add their cards here */}
      {/* <SalesforceIntegrationCard ... /> */}
      {/* <SquareIntegrationCard ... /> */}

      {/* Other CRM Setup */}
      {!showOnlyWebhook && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Other Integrations</Text>
            <TouchableOpacity
              style={styles.addButton}
              onPress={() => setShowSetupForm(!showSetupForm)}
            >
              <Text style={styles.addButtonText}>
                {showSetupForm ? "Cancel" : "+ Add Integration"}
              </Text>
            </TouchableOpacity>
          </View>

          {showSetupForm && (
            <IntegrationSetupForm session={session} onSuccess={refreshData} />
          )}
        </View>
      )}

      {/* Existing Integrations */}
      {!showOnlyWebhook && (
        <IntegrationManager session={session} onChange={refreshData} />
      )}

      {/* Debug info - remove this in production */}
      {process.env.EXPO_PUBLIC_ENV === "local" && !showOnlyWebhook && (
        <View style={styles.debugSection}>
          <Text style={styles.debugText}>
            Debug: URL params - Top Aesthetics: {showTopAesthetics.toString()},
            Cameo Surgery: {showCameoSurgery.toString()}
          </Text>
          <Text style={styles.debugText}>
            Available params: ?top-aesthetics, ?cameo, ?all
          </Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: "#f8f9fa",
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#212529",
  },
  subtitle: {
    fontSize: 16,
    color: "#6c757d",
    marginBottom: 24,
  },
  errorText: {
    textAlign: "center",
    color: "#dc3545",
    fontSize: 16,
    marginTop: 50,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#495057",
  },
  addButton: {
    backgroundColor: "#28a745",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  addButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "600",
  },
  debugSection: {
    marginTop: 20,
    padding: 10,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#dee2e6",
  },
  debugText: {
    fontSize: 12,
    color: "#6c757d",
    marginBottom: 4,
  },
});

export default OAuthInitiatorPage;
